/**
 * CollegesView - College-Grouped Task View
 * Groups all tasks by college with rollup cards showing progress
 */

import React, { useState, useMemo } from 'react';
import { School, ChevronDown, ChevronRight, Calendar, CheckCircle, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import CollegeCard from './CollegeCard';
import { calculateProgress, isOverdue, getDaysUntilDue } from '../../utils/taskUtils';

const CollegesView = ({
  timeline = [],
  colleges = [],
  onTaskClick,
  onTaskComplete,
  onAISupport,
  className = ''
}) => {
  const [expandedColleges, setExpandedColleges] = useState(new Set());
  const [showCompleted, setShowCompleted] = useState(false);

  // Group tasks by college
  const collegeGroups = useMemo(() => {
    const groups = {};
    const crossCollegeTasks = [];

    timeline.forEach(task => {
      if (!task.college_id) {
        // Cross-college task (Common App essay, etc.)
        crossCollegeTasks.push(task);
      } else {
        if (!groups[task.college_id]) {
          groups[task.college_id] = {
            college: colleges.find(c => c.id === task.college_id) || {
              id: task.college_id,
              name: task.college_name || 'Unknown College'
            },
            tasks: []
          };
        }
        groups[task.college_id].tasks.push(task);
      }
    });

    // Calculate stats for each college
    Object.values(groups).forEach(group => {
      const allTasks = group.tasks;
      const completedTasks = allTasks.filter(t => t.status === 'completed');
      const overdueTasks = allTasks.filter(t => isOverdue(t));

      // Category breakdown
      const categoryBreakdown = {};
      allTasks.forEach(task => {
        const cat = task.category || 'other';
        if (!categoryBreakdown[cat]) {
          categoryBreakdown[cat] = { completed: 0, total: 0 };
        }
        categoryBreakdown[cat].total++;
        if (task.status === 'completed') {
          categoryBreakdown[cat].completed++;
        }
      });

      // Find next task
      const incompleteTasks = allTasks
        .filter(t => t.status !== 'completed')
        .sort((a, b) => {
          // Sort by: overdue first, then by due date
          const aOverdue = isOverdue(a);
          const bOverdue = isOverdue(b);
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;

          if (a.due_date && b.due_date) {
            return new Date(a.due_date) - new Date(b.due_date);
          }
          return 0;
        });

      group.stats = {
        total: allTasks.length,
        completed: completedTasks.length,
        percentage: allTasks.length > 0 ? Math.round((completedTasks.length / allTasks.length) * 100) : 0,
        overdue: overdueTasks.length,
        categoryBreakdown,
        nextTask: incompleteTasks[0] || null
      };
    });

    return {
      byCollege: groups,
      crossCollege: crossCollegeTasks
    };
  }, [timeline, colleges]);

  const toggleCollege = (collegeId) => {
    const newExpanded = new Set(expandedColleges);
    if (newExpanded.has(collegeId)) {
      newExpanded.delete(collegeId);
    } else {
      newExpanded.add(collegeId);
    }
    setExpandedColleges(newExpanded);
  };

  // Sort colleges by deadline (earliest first)
  const sortedColleges = useMemo(() => {
    return Object.values(collegeGroups.byCollege).sort((a, b) => {
      const aDeadline = a.college.global_colleges?.rd_deadline ||
                       a.college.global_colleges?.ed_deadline ||
                       a.college.global_colleges?.ea_deadline;
      const bDeadline = b.college.global_colleges?.rd_deadline ||
                       b.college.global_colleges?.ed_deadline ||
                       b.college.global_colleges?.ea_deadline;

      if (!aDeadline && !bDeadline) return 0;
      if (!aDeadline) return 1;
      if (!bDeadline) return -1;

      return new Date(aDeadline) - new Date(bDeadline);
    });
  }, [collegeGroups]);

  // Filter out completed colleges if showCompleted is false
  const visibleColleges = showCompleted
    ? sortedColleges
    : sortedColleges.filter(group => group.stats.percentage < 100);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <School className="w-6 h-6 text-indigo-600" />
          Colleges ({visibleColleges.length})
        </h2>

        {/* Show Completed Toggle */}
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <span>Show completed colleges</span>
        </label>
      </div>

      {/* Cross-College Tasks (if any) */}
      {collegeGroups.crossCollege.length > 0 && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Cross-College Tasks ({collegeGroups.crossCollege.length})
          </h3>
          <p className="text-sm text-purple-700 mb-3">
            These tasks apply to multiple colleges (like your Common App essay)
          </p>
          <div className="space-y-2">
            {collegeGroups.crossCollege.slice(0, 5).map(task => (
              <div
                key={task.id}
                onClick={() => onTaskClick && onTaskClick(task)}
                className="bg-white p-3 rounded border border-purple-200 cursor-pointer hover:bg-purple-50 transition-colors"
              >
                <p className="font-medium text-sm text-gray-900">{task.title}</p>
                {task.due_date && (
                  <p className="text-xs text-gray-600 mt-1">
                    Due {format(parseISO(task.due_date), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* College Cards */}
      {visibleColleges.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <School className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium mb-2">No colleges with tasks</h3>
          <p className="text-sm">
            {showCompleted
              ? 'Add colleges to see their tasks here'
              : 'All colleges are complete! Toggle "Show completed" to see them'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleColleges.map(group => (
            <CollegeCard
              key={group.college.id}
              college={group.college}
              stats={group.stats}
              tasks={group.tasks}
              isExpanded={expandedColleges.has(group.college.id)}
              onToggle={() => toggleCollege(group.college.id)}
              onTaskClick={onTaskClick}
              onTaskComplete={onTaskComplete}
              onAISupport={onAISupport}
            />
          ))}
        </div>
      )}

      {/* Summary Footer */}
      {visibleColleges.length > 0 && (
        <div className="text-center text-sm text-gray-600 pt-4 border-t border-gray-200">
          Showing {visibleColleges.length} college{visibleColleges.length !== 1 ? 's' : ''} with{' '}
          {Object.values(collegeGroups.byCollege).reduce((sum, g) => sum + g.tasks.length, 0)} total tasks
        </div>
      )}
    </div>
  );
};

export default CollegesView;
